import {
    Component,
    Inject,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewEncapsulation,
    DOCUMENT,
    ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { HippoConfig, HippoConfigService } from '@hippo/services/config';
import { HippoMediaWatcherService } from '@hippo/services/media-watcher';
import { HippoPlatformService } from '@hippo/services/platform';
import { HIPPO_VERSION } from '@hippo/version';
import {
    combineLatest,
    filter,
    lastValueFrom,
    map,
    Subject,
    takeUntil,
} from 'rxjs';
import { ClassicLayoutComponent } from './layouts/classic/classic.component';
import { EmptyLayoutComponent } from './layouts/empty/empty.component';
import { MyDataService } from 'app/modules/admin/team/my-data/my-data.service';
import { AuthService } from 'app/core/auth/auth.service';
import { SharedService } from 'app/core/services/shared.service';
import { HyperlinksService } from 'app/core/services/hyperlinks.service';
import { AutoLogoutService } from 'app/core/services/auto-logout.service';
import { UserService } from 'app/core/services/user.service';

@Component({
    selector: 'layout',
    templateUrl: './layout.component.html',
    styleUrls: ['./layout.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [ClassicLayoutComponent, EmptyLayoutComponent],
})
export class LayoutComponent implements OnInit, OnDestroy {
    config: HippoConfig;
    layout: string;
    scheme: 'dark' | 'light';
    theme: string;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        @Inject(DOCUMENT) private _document: any,
        private _renderer2: Renderer2,
        private _router: Router,
        private _hippoConfigService: HippoConfigService,
        private _hippoMediaWatcherService: HippoMediaWatcherService,
        private _hippoPlatformService: HippoPlatformService,
        private myDataService: MyDataService,
        private authService: AuthService,
        private sharedService: SharedService,
        private hyperlinksService: HyperlinksService,
        private autoLogoutService: AutoLogoutService,
        private userService: UserService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    async ngOnInit() {
        if (await lastValueFrom(this.authService.check())) {
            if (!this.userService.user.request_new) {
                await this.myDataService.loadUserData();
                await this.hyperlinksService.getHyperlinks();
                this.sharedService.apiSystemLanguage =
                    this.myDataService.userData.user_settings_language;
            }

            this.sharedService.checkTouchDevice();
            this.autoLogoutService.startTimer();
        }

        // Set the theme and scheme based on the configuration
        combineLatest([
            this._hippoConfigService.config$,
            this._hippoMediaWatcherService.onMediaQueryChange$([
                '(prefers-color-scheme: dark)',
                '(prefers-color-scheme: light)',
            ]),
        ])
            .pipe(
                takeUntil(this._unsubscribeAll),
                map(([config, mql]) => {
                    const options = {
                        scheme: config.scheme,
                        theme: config.theme,
                    };

                    // If the scheme is set to 'auto'...
                    if (config.scheme === 'auto') {
                        // Decide the scheme using the media query
                        options.scheme = mql.breakpoints[
                            '(prefers-color-scheme: dark)'
                        ]
                            ? 'dark'
                            : 'light';
                    }

                    return options;
                })
            )
            .subscribe(options => {
                // Store the options
                this.scheme = options.scheme;
                this.theme = options.theme;

                // Update the scheme and theme
                this._updateScheme();
                this._updateTheme();
            });

        // Subscribe to config changes
        this._hippoConfigService.config$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((config: HippoConfig) => {
                // Store the config
                this.config = config;

                // Update the layout
                this._updateLayout();
            });

        // Subscribe to NavigationEnd event
        this._router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => {
                // Update the layout
                this._updateLayout();
            });

        // Set the app version
        this._renderer2.setAttribute(
            this._document.querySelector('[ng-version]'),
            'hippo-version',
            HIPPO_VERSION
        );

        // Set the OS name
        this._renderer2.addClass(
            this._document.body,
            this._hippoPlatformService.osName
        );
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the selected layout
     */
    private _updateLayout(): void {
        // Get the current activated route
        let route = this._activatedRoute;
        while (route.firstChild) {
            route = route.firstChild;
        }

        // 1. Set the layout from the config
        this.layout = this.config.layout;

        // 2. Get the query parameter from the current route and
        // set the layout and save the layout to the config
        const layoutFromQueryParam = route.snapshot.queryParamMap.get('layout');
        if (layoutFromQueryParam) {
            this.layout = layoutFromQueryParam;
            if (this.config) {
                this.config.layout = layoutFromQueryParam;
            }
        }

        // 3. Iterate through the paths and change the layout as we find
        // a config for it.
        //
        // The reason we do this is that there might be empty grouping
        // paths or componentless routes along the path. Because of that,
        // we cannot just assume that the layout configuration will be
        // in the last path's config or in the first path's config.
        //
        // So, we get all the paths that matched starting from root all
        // the way to the current activated route, walk through them one
        // by one and change the layout as we find the layout config. This
        // way, layout configuration can live anywhere within the path and
        // we won't miss it.
        //
        // Also, this will allow overriding the layout in any time so we
        // can have different layouts for different routes.
        const paths = route.pathFromRoot;
        paths.forEach(path => {
            // Check if there is a 'layout' data
            if (
                path.routeConfig &&
                path.routeConfig.data &&
                path.routeConfig.data.layout
            ) {
                // Set the layout
                this.layout = path.routeConfig.data.layout;
            }
        });
    }

    /**
     * Update the selected scheme
     *
     * @private
     */
    private _updateScheme(): void {
        // Remove class names for all schemes
        this._document.body.classList.remove('light', 'dark');

        // Add class name for the currently selected scheme
        this._document.body.classList.add(this.scheme);
    }

    /**
     * Update the selected theme
     *
     * @private
     */
    private _updateTheme(): void {
        // Find the class name for the previously selected theme and remove it
        this._document.body.classList.forEach((className: string) => {
            if (className.startsWith('theme-')) {
                this._document.body.classList.remove(
                    className,
                    className.split('-')[1]
                );
            }
        });

        // Add class name for the currently selected theme
        this._document.body.classList.add(this.theme);
    }
}
